import { ArrowRight, Moon, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/theme/useTheme';
import { KanameMark } from '@/components/brand/KanameMark';
import { IconButton } from '@/components/ui';
import { ColorsSection } from './sections/ColorsSection';
import { TypographySection } from './sections/TypographySection';
import { LayersSection, RadiusSection, ShadowsSection, SpacingSection } from './sections/GeometrySection';
import { MotionSection } from './sections/MotionSection';
import { PrimitivesSection } from './sections/PrimitivesSection';
import { DataPrimitivesSection } from './sections/DataPrimitivesSection';
import { BrandSection } from './sections/BrandSection';
import { ShellSection } from './sections/ShellSection';
import { ShellTokensSection } from './sections/ShellTokensSection';
import { WorkspacePatternsSection } from './sections/WorkspacePatternsSection';
import { ConversationPatternsSection } from './sections/ConversationPatternsSection';
import { ProgressPatternsSection } from './sections/ProgressPatternsSection';
import { ObjectFormsSection } from './sections/ObjectFormsSection';
import { ObjectRelationsSection } from './sections/ObjectRelationsSection';
import { ObjectViewsSection } from './sections/ObjectViewsSection';
import { HomePatternsSection } from './sections/HomePatternsSection';
import { HomeWidgetTypesSection } from './sections/HomeWidgetTypesSection';
import { ObjectPatternsSection } from './sections/ObjectPatternsSection';
import { ChartPatternsSection } from './sections/ChartPatternsSection';
import { FieldPatternsSection } from './sections/FieldPatternsSection';
import { CommentPatternsSection } from './sections/CommentPatternsSection';
import { DutyPatternsSection } from './sections/DutyPatternsSection';
import { OperationsPatternsSection } from './sections/OperationsPatternsSection';

/*
  /design — живая галерея дизайн-системы (frontend/DESIGN.md, спека §4.4).
  Публичный маршрут без данных: импортирует настоящие токены и компоненты, поэтому не устаревает.
  Служит мишенью для скриншот-тестов и местом, где новая сущность обязана появиться (R16).
*/

/** Страницы-спутники галереи: собранный сайт, вход, витрина и приёмы. */
const PAGES = [
  { to: '/design/foundation', title: 'Основа' },
  { to: '/design/login', title: 'Вход' },
  { to: '/design/showcase', title: 'Витрина' },
  { to: '/design/character', title: 'Характер' },
  { to: '/design/observer', title: 'Наблюдатель' },
];

const SECTIONS = [
  { id: 'colors', title: 'Цвета' },
  { id: 'type', title: 'Типографика' },
  { id: 'space', title: 'Отступы' },
  { id: 'radius', title: 'Радиусы' },
  { id: 'shadow', title: 'Тени' },
  { id: 'layers', title: 'Слои' },
  { id: 'motion', title: 'Motion' },
  { id: 'primitives', title: 'Примитивы' },
  { id: 'data', title: 'Данные и формы' },
  { id: 'brand', title: 'Знак и бренд' },
  { id: 'shell', title: 'Оболочка' },
];

export function DesignGallery() {
  const { mode, toggle } = useTheme();

  return (
    <div className="h-screen overflow-y-auto bg-frame text-fg">
      <header className="sticky top-0 z-raised border-b border-line bg-canvas/90 backdrop-blur">
        {/* Две строки: сверху знак, название и страницы-спутники, снизу разделы галереи. В одну
            строку всё не помещается — заголовок сжимался в столбик. */}
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-8 pt-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <KanameMark className="size-8 shrink-0" label="Kaname" />
            <div className="min-w-0">
              <h1 className="truncate type-heading text-fg">Дизайн-система Kaname</h1>
              <p className="truncate text-xs text-fg-muted">
                Живая галерея токенов и примитивов · frontend/DESIGN.md
              </p>
            </div>
          </div>
          <nav className="flex flex-wrap items-center gap-1" aria-label="Страницы">
            {PAGES.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium text-brand-fg transition-colors duration-fast hover:bg-brand-soft"
              >
                {p.title}
                <ArrowRight className="size-3.5" />
              </Link>
            ))}
            <IconButton
              aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
              variant="secondary"
              onClick={toggle}
            >
              {mode === 'dark' ? <Sun /> : <Moon />}
            </IconButton>
          </nav>
        </div>
        <nav
          className="mx-auto scrollbar-none flex max-w-6xl items-center gap-1 overflow-x-auto px-6 py-2"
          aria-label="Разделы"
        >
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="shrink-0 rounded-sm px-2 py-1 text-xs font-medium text-fg-muted transition-colors duration-fast hover:bg-hover hover:text-fg"
            >
              {s.title}
            </a>
          ))}
        </nav>
      </header>

      <main className="mx-auto flex max-w-6xl flex-col gap-16 px-8 py-10">
        <ColorsSection />
        <TypographySection />
        <SpacingSection />
        <RadiusSection />
        <ShadowsSection />
        <LayersSection />
        <MotionSection />
        <PrimitivesSection />
        <DataPrimitivesSection />
        <BrandSection />
        <ShellSection />
        <ShellTokensSection />
        <WorkspacePatternsSection />
        <OperationsPatternsSection />
        <ConversationPatternsSection />
        <ProgressPatternsSection />
        <DutyPatternsSection />
        <CommentPatternsSection />
        <FieldPatternsSection />
        <ChartPatternsSection />
        <ObjectPatternsSection />
        <ObjectFormsSection />
        <ObjectRelationsSection />
        <ObjectViewsSection />
        <div className="[&>section]:scroll-mt-28">
          <HomePatternsSection />
        </div>
        <HomeWidgetTypesSection />
      </main>
    </div>
  );
}
